import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import GeoAddress from "../components/GeoAddress";
import {
  ShieldCheck, Mail, Smartphone, Loader2, ArrowRight, KeyRound, ArrowLeft,
  Users, Briefcase, GraduationCap, BookOpen, HeartHandshake, Upload, FileText, Trash2, Building2, Eye,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BRAND } from "../mock";

const PORTALS = [
  { key: "staff", title: "Employees & Admin", desc: "Counsellors, staff and administrators", icon: Users, ut: "staff" },
  { key: "agent", title: "Agents", desc: "Partner agent portal", icon: Briefcase, ut: "agent", canRegister: true, isAgent: true },
  { key: "student", title: "Students", desc: "Track applications & register", icon: GraduationCap, ut: "student", canRegister: true },
  { key: "teacher", title: "Teachers", desc: "Curriculum & content portal", icon: BookOpen, ut: "teacher" },
  { key: "parent", title: "Parents", desc: "Follow your child's journey", icon: HeartHandshake, ut: "parent", canRegister: true, isParent: true },
];

const redirectFor = (kind) => {
  if (kind === "student") return "/portal/student";
  if (kind === "teacher") return "/portal/teacher";
  if (kind === "agent") return "/portal/agent";
  if (kind === "parent") return "/portal/parent";
  return "/admin";
};

export default function PortalLogin() {
  const nav = useNavigate();
  const { finishLogin } = useAuth();
  const [portal, setPortal] = useState(null);
  const [staffKind, setStaffKind] = useState("super_admin");
  const [mode, setMode] = useState("login"); // login | register | otp
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [creds, setCreds] = useState({ identifier: "", password: "" });
  const [otpState, setOtpState] = useState(null);
  const [otp, setOtp] = useState({ email_otp: "", phone_otp: "" });
  const [reg, setReg] = useState({ name: "", agency_name: "", email: "", dial_code: "+60", phone: "", password: "", country: "", country_code: "", interest: "", agent_code: "DIRECT", student_code: "", company_id: "", agency_address: {}, contact_person: "", contact_person_email: "", contact_person_dial: "+60", contact_person_phone: "", experience_docs: [], students_placed: "", new_to_field: "No", previous_business: "", experience_details: "" });
  const [dial, setDial] = useState([]);
  const [agents, setAgents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [agentStep, setAgentStep] = useState("form");
  const [agentOtp, setAgentOtp] = useState({ email_code: "", phone_code: "", contact_email_code: "", email_demo: "", phone_demo: "", contact_email_demo: "", contact_requested: false });
  const [notice, setNotice] = useState("");
  const [verifyInfo, setVerifyInfo] = useState(null); // { email, demo_code }
  const [emailOtp, setEmailOtp] = useState("");
  const [justRegistered, setJustRegistered] = useState(false);

  useEffect(() => {
    api.get("/auth/dial-codes").then((r) => setDial(r.data)).catch(() => {});
    api.get("/public/agents").then((r) => setAgents(r.data)).catch(() => {});
    api.get("/public/companies").then((r) => setCompanies(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReg((r) => ({ ...r, agent_code: ref }));
    const pk = params.get("portal");
    if (pk) {
      const p = PORTALS.find((x) => x.key === pk);
      if (p) { setPortal(p); if (params.get("mode") === "register" && p.canRegister) setMode("register"); }
    }
  }, []);

  const userType = portal?.key === "staff" ? staffKind : portal?.ut;

  const choose = (p) => { setPortal(p); setMode("login"); setErr(""); setNotice(""); setAgentStep("form"); setCreds({ identifier: "", password: "" }); };

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  const onAgentCert = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 6 * 1024 * 1024) { setErr(`"${file.name}" is larger than 6MB`); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const kind = file.type.startsWith("image") ? "image" : "file";
        setReg((r) => ({ ...r, experience_docs: [...(r.experience_docs || []), { id: Math.random().toString(36).slice(2), name: file.name, data: reader.result, kind }] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeAgentCert = (id) => setReg((r) => ({ ...r, experience_docs: (r.experience_docs || []).filter((d) => d.id !== id) }));

  const verifyAgent = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/auth/agent-register/verify", {
        name: reg.name, agency_name: reg.agency_name, email: reg.email, dial_code: reg.dial_code, phone: reg.phone,
        password: reg.password, company_id: reg.company_id, agency_address: reg.agency_address,
        contact_person: reg.contact_person, contact_person_email: reg.contact_person_email,
        contact_person_dial: reg.contact_person_dial, contact_person_phone: reg.contact_person_phone,
        email_code: agentOtp.email_code, phone_code: agentOtp.phone_code, contact_email_code: agentOtp.contact_email_code,
        experience_docs: reg.experience_docs || [], students_placed: parseInt(reg.students_placed || 0) || 0,
        new_to_field: reg.new_to_field, previous_business: reg.previous_business, experience_details: reg.experience_details,
      });
      setCreds({ identifier: reg.email, password: "" });
      setMode("login"); setAgentStep("form");
      setNotice("Email & mobile verified. Your application is submitted and pending admin approval.");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { ...creds, user_type: userType });
      if (data.requires_otp) { setOtpState(data); setMode("otp"); }
      else { finishLogin(data.token, data.user); nav(redirectFor(data.user.kind)); }
    } catch (e2) {
      const detail = e2?.response?.data?.detail || "Login failed";
      if (detail === "EMAIL_NOT_VERIFIED") {
        setVerifyInfo({ email: creds.identifier });
        setMode("verify");
        setErr("");
        try { const r = await api.post("/auth/resend-verification", { email: creds.identifier }); if (r.data?.demo_verify_code) setVerifyInfo({ email: creds.identifier, demo_code: r.data.demo_verify_code }); } catch (_) {}
      } else { setErr(detail); }
    }
    finally { setLoading(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { temp_token: otpState.temp_token, ...otp });
      finishLogin(data.token, data.user);
      nav(data.user.kind === "student" && justRegistered ? "/portal/student?tab=profile" : redirectFor(data.user.kind));
    } catch (e2) { setErr(e2?.response?.data?.detail || "Verification failed"); }
    finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      if (portal?.isAgent) {
        if (reg.contact_person_email && !emailValid(reg.contact_person_email)) { setErr("Please enter a valid contact person email"); setLoading(false); return; }
        if (!emailValid(reg.email)) { setErr("Please enter a valid agency email"); setLoading(false); return; }
        const { data } = await api.post("/auth/agent-register/request", { email: reg.email, dial_code: reg.dial_code, phone: reg.phone, contact_person_email: reg.contact_person_email });
        setAgentOtp({ email_code: "", phone_code: "", contact_email_code: "", email_demo: data.email_demo || "", phone_demo: data.phone_demo || "", contact_email_demo: data.contact_email_demo || "", contact_requested: !!data.contact_email_requested });
        setAgentStep("otp");
        setErr("");
        return;
      }
      if (portal?.isParent) {
        await api.post("/auth/register-parent", { name: reg.name, email: reg.email, dial_code: reg.dial_code, phone: reg.phone, password: reg.password, student_code: reg.student_code });
        setCreds({ identifier: reg.email, password: "" });
        setMode("login");
        setErr("");
        return;
      }
      const { data } = await api.post("/auth/register-student", { ...reg, origin: window.location.origin });
      setJustRegistered(true);
      if (data.requires_verification) {
        setVerifyInfo({ email: reg.email, demo_code: data.demo_verify_code, student_code: data.student_code });
        setCreds({ identifier: reg.email, password: "" });
        setMode("verify");
      } else { setCreds({ identifier: reg.email, password: "" }); setMode("login"); }
      setErr("");
    } catch (e2) { setErr(e2?.response?.data?.detail || "Registration failed"); }
    finally { setLoading(false); }
  };

  const doVerifyEmail = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/auth/verify-email", { token: emailOtp.trim() });
      setEmailOtp(""); setVerifyInfo(null); setMode("login");
      setErr("");
    } catch (e2) { setErr(e2?.response?.data?.detail || "Verification failed"); }
    finally { setLoading(false); }
  };

  const resendVerify = async () => {
    try { const r = await api.post("/auth/resend-verification", { email: verifyInfo?.email }); setVerifyInfo({ ...verifyInfo, demo_code: r.data?.demo_verify_code }); } catch (_) {}
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f4f8f6]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#004030] p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl" />
        <Link to="/" className="relative self-start" data-testid="login-logo-home"><img src={BRAND.logo} alt="GDIH" className="h-20 w-auto object-contain bg-white/95 rounded-xl p-2.5" /></Link>
        <div className="relative">
          <h1 className="font-display text-white text-4xl font-bold leading-tight">One portal.<br />Every journey.</h1>
          <p className="text-white/70 mt-4 max-w-md">Staff, agents, students and teachers — each with a secure, dedicated sign-in.</p>
          <div className="flex items-center gap-2 mt-8 text-[#e0c05a] text-sm"><ShieldCheck size={18} /> Email &amp; phone verified access</div>
          <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-white/80 hover:text-white text-sm font-medium" data-testid="login-back-home"><ArrowLeft size={16} /> Back to website</Link>
        </div>
        <p className="relative text-white/40 text-xs">© {new Date().getFullYear()} {BRAND.legal}</p>
      </div>

      {/* Right */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-6"><Link to="/"><img src={BRAND.logo} alt="GDIH" className="h-16" /></Link></div>

          {new URLSearchParams(window.location.search).get("expired") && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 mb-4 text-sm" data-testid="session-expired-notice">
              Your session expired. Please sign in again to continue.
            </div>
          )}

          {/* Portal chooser */}
          {!portal && (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
              <h2 className="font-display text-2xl font-bold text-[#012a1e]">Choose your portal</h2>
              <p className="text-slate-500 text-sm mt-1">Select how you'd like to sign in</p>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {PORTALS.map((p) => (
                  <button key={p.key} onClick={() => choose(p)}
                    className="group text-left border border-slate-200 rounded-xl p-4 hover:border-[#004030] hover:bg-[#f7faf8] transition-colors">
                    <div className="w-11 h-11 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030] group-hover:bg-[#004030] group-hover:text-[#e0c05a] transition-colors">
                      <p.icon size={22} />
                    </div>
                    <p className="font-semibold text-[#012a1e] mt-3 text-sm">{p.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Login / Register form */}
          {portal && mode !== "otp" && mode !== "verify" && (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
              <button onClick={() => setPortal(null)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004030] mb-4"><ArrowLeft size={16} /> All portals</button>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a]"><portal.icon size={22} /></div>
                <div>
                  <h2 className="font-display text-xl font-bold text-[#012a1e]">{portal.title}</h2>
                  <p className="text-slate-500 text-xs">{mode === "register" ? (portal.isAgent ? "Register as a partner agent" : portal.isParent ? "Create your parent account" : "Create your student account") : "Sign in to continue"}</p>
                </div>
              </div>

              {portal.key === "staff" && (
                <div className="grid grid-cols-2 gap-1 bg-[#f0f6f3] rounded-full p-1 mt-5">
                  {[{ k: "super_admin", l: "Administrator" }, { k: "employee", l: "Employee" }].map((s) => (
                    <button key={s.k} onClick={() => setStaffKind(s.k)}
                      className={`text-sm font-medium py-2 rounded-full transition-colors ${staffKind === s.k ? "bg-[#004030] text-white" : "text-[#004030]"}`}>{s.l}</button>
                  ))}
                </div>
              )}

              {portal.canRegister && (
                <div className="grid grid-cols-2 gap-1 bg-[#f0f6f3] rounded-full p-1 mt-5">
                  {[{ k: "login", l: "Sign In" }, { k: "register", l: "Register" }].map((s) => (
                    <button key={s.k} onClick={() => { setMode(s.k); setErr(""); }}
                      className={`text-sm font-medium py-2 rounded-full transition-colors ${mode === s.k ? "bg-[#004030] text-white" : "text-[#004030]"}`}>{s.l}</button>
                  ))}
                </div>
              )}

              {mode === "login" ? (
                <form onSubmit={doLogin} className="mt-5 space-y-4">
                  {notice && <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm" data-testid="register-notice">{notice}</div>}
                  <div className="space-y-2">
                    <Label>{portal.key === "staff" && staffKind === "super_admin" ? "Username" : "Email"}</Label>
                    <Input value={creds.identifier} onChange={(e) => setCreds({ ...creds, identifier: e.target.value })}
                      placeholder={portal.key === "staff" && staffKind === "super_admin" ? "founder" : "you@email.com"} className="h-12 focus-visible:ring-[#004030]" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="••••••••" className="h-12 focus-visible:ring-[#004030]" />
                  </div>
                  {err && <p className="text-sm text-red-600">{err}</p>}
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : <>Continue <ArrowRight size={17} className="ml-1" /></>}
                  </Button>
                </form>
              ) : portal.isAgent ? (
                agentStep === "otp" ? (
                  <form onSubmit={verifyAgent} className="mt-5 space-y-3.5" data-testid="agent-otp-form">
                    <p className="text-sm text-slate-600">We sent verification codes to <b>{reg.email}</b> and <b>{reg.dial_code} {reg.phone}</b>. Enter both to submit your application.</p>
                    <div className="space-y-1"><Label className="text-xs text-slate-500">Email code{agentOtp.email_demo ? <span className="text-[#c9a227] font-semibold"> · demo {agentOtp.email_demo}</span> : ""}</Label>
                      <Input value={agentOtp.email_code} onChange={(e) => setAgentOtp({ ...agentOtp, email_code: e.target.value })} placeholder="Email code" className="h-11" data-testid="agent-email-code" required /></div>
                    <div className="space-y-1"><Label className="text-xs text-slate-500">Mobile code{agentOtp.phone_demo ? <span className="text-[#c9a227] font-semibold"> · demo {agentOtp.phone_demo}</span> : ""}</Label>
                      <Input value={agentOtp.phone_code} onChange={(e) => setAgentOtp({ ...agentOtp, phone_code: e.target.value })} placeholder="Mobile code" className="h-11" data-testid="agent-phone-code" required /></div>
                    {agentOtp.contact_requested && (
                      <div className="space-y-1"><Label className="text-xs text-slate-500">Contact-person email code — sent to {reg.contact_person_email}{agentOtp.contact_email_demo ? <span className="text-[#c9a227] font-semibold"> · demo {agentOtp.contact_email_demo}</span> : ""}</Label>
                        <Input value={agentOtp.contact_email_code} onChange={(e) => setAgentOtp({ ...agentOtp, contact_email_code: e.target.value })} placeholder="Contact person email code" className="h-11" data-testid="agent-contact-email-code" required /></div>
                    )}
                    {err && <p className="text-sm text-red-600">{err}</p>}
                    <Button type="submit" disabled={loading} data-testid="agent-verify-submit" className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">{loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & submit application"}</Button>
                    <button type="button" onClick={() => setAgentStep("form")} className="w-full text-sm text-slate-500 hover:text-[#004030]">Back</button>
                  </form>
                ) : (
                  <form onSubmit={doRegister} className="mt-5 space-y-3.5" data-testid="agent-register-form">
                    <Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="Your full name" className="h-11" data-testid="agent-reg-name" required />
                    <div className="space-y-1"><Label className="text-xs text-slate-500">GDIH company you're partnering with</Label>
                      <Select value={reg.company_id} onValueChange={(v) => setReg({ ...reg, company_id: v })}>
                        <SelectTrigger className="h-11" data-testid="agent-reg-company"><SelectValue placeholder="Select GDIH company" /></SelectTrigger>
                        <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                      {reg.company_id && (() => { const c = companies.find((x) => x.id === reg.company_id); return c ? (
                        <div className="mt-2 rounded-xl border border-[#004030]/15 bg-[#f0f6f3] p-3 text-xs text-[#012a1e] space-y-0.5" data-testid="agent-company-details">
                          <p className="flex items-center gap-1.5 font-semibold text-[#004030]"><Building2 size={13} /> {c.name}</p>
                          {c.address && <p className="text-slate-600">{c.address}</p>}
                          <p className="text-slate-600">{[c.country_name, c.email, c.phone].filter(Boolean).join(" · ")}</p>
                          {c.website && <p className="text-slate-500">{c.website}</p>}
                        </div>
                      ) : null; })()}
                    </div>
                    <div className="border-t border-slate-100 pt-3"><Label className="text-xs text-slate-500 font-semibold">Your agency details</Label></div>
                    <Input value={reg.agency_name} onChange={(e) => setReg({ ...reg, agency_name: e.target.value })} placeholder="Agency / company name" className="h-11" data-testid="agent-reg-agency" />
                    <GeoAddress value={reg.agency_address} prefix="agency-addr" onChange={(v) => setReg({ ...reg, agency_address: v })} />
                    <Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="Agency email (login — will be verified)" className="h-11" data-testid="agent-reg-email" required />
                    <div className="flex gap-2">
                      <Select value={reg.dial_code} onValueChange={(v) => setReg({ ...reg, dial_code: v })}>
                        <SelectTrigger className="h-11 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{dial.map((dc, i) => <SelectItem key={`adial-${dc.code}-${i}`} value={dc.dial}>{dc.flag} {dc.dial}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} placeholder="Mobile (will be verified)" className="h-11 flex-1" data-testid="agent-reg-phone" required />
                    </div>
                    <div className="border-t border-slate-100 pt-3"><Label className="text-xs text-slate-500 font-semibold">Contact person</Label></div>
                    <Input value={reg.contact_person} onChange={(e) => setReg({ ...reg, contact_person: e.target.value })} placeholder="Contact person name" className="h-11" data-testid="agent-reg-contact" />
                    <Input type="email" value={reg.contact_person_email} onChange={(e) => setReg({ ...reg, contact_person_email: e.target.value })} placeholder="Contact person email (will be verified)" className={`h-11 ${reg.contact_person_email && !emailValid(reg.contact_person_email) ? "border-red-400" : ""}`} data-testid="agent-reg-contact-email" />
                    {reg.contact_person_email && !emailValid(reg.contact_person_email) && <p className="text-[11px] text-red-500 -mt-2">Enter a valid email — a code will be sent here to verify.</p>}
                    <div className="flex gap-2">
                      <Select value={reg.contact_person_dial} onValueChange={(v) => setReg({ ...reg, contact_person_dial: v })}>
                        <SelectTrigger className="h-11 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{dial.map((dc, i) => <SelectItem key={`cdial-${dc.code}-${i}`} value={dc.dial}>{dc.flag} {dc.dial}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={reg.contact_person_phone} onChange={(e) => setReg({ ...reg, contact_person_phone: e.target.value })} placeholder="Contact person mobile" className="h-11 flex-1" data-testid="agent-reg-contact-phone" />
                    </div>

                    <div className="border-t border-slate-100 pt-3"><Label className="text-xs text-slate-500 font-semibold">Experience (optional)</Label></div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" value={reg.students_placed} onChange={(e) => setReg({ ...reg, students_placed: e.target.value })} placeholder="Students placed" className="h-11" data-testid="agent-reg-students-placed" />
                      <Select value={reg.new_to_field} onValueChange={(v) => setReg({ ...reg, new_to_field: v })}>
                        <SelectTrigger className="h-11" data-testid="agent-reg-newfield"><SelectValue placeholder="New to this field?" /></SelectTrigger>
                        <SelectContent><SelectItem value="No">Experienced</SelectItem><SelectItem value="Yes">New to this field</SelectItem></SelectContent>
                      </Select>
                    </div>
                    {reg.new_to_field === "Yes"
                      ? <Input value={reg.previous_business} onChange={(e) => setReg({ ...reg, previous_business: e.target.value })} placeholder="Your earlier line of business" className="h-11" data-testid="agent-reg-prevbiz" />
                      : <Input value={reg.experience_details} onChange={(e) => setReg({ ...reg, experience_details: e.target.value })} placeholder="Experience details (past placements etc.)" className="h-11" data-testid="agent-reg-expdetails" />}
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Past-experience certificates (photo or file)</Label>
                      <label className="flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed border-[#004030]/30 text-[#004030] text-sm font-semibold cursor-pointer hover:bg-[#f0f6f3]" data-testid="agent-reg-cert-upload">
                        <Upload size={15} /> Take photo / upload
                        <input type="file" accept="image/*,application/pdf,.doc,.docx" capture="environment" multiple hidden onChange={onAgentCert} />
                      </label>
                      {(reg.experience_docs || []).length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {reg.experience_docs.map((d) => (
                            <div key={d.id} className="relative border border-slate-200 rounded-lg p-1.5 text-center" data-testid={`agent-reg-cert-${d.id}`}>
                              {d.kind === "image" ? <img src={d.data} alt={d.name} className="h-12 w-full object-cover rounded" /> : <FileText size={22} className="mx-auto text-[#004030] my-1" />}
                              <p className="text-[9px] truncate mt-0.5">{d.name}</p>
                              <div className="flex items-center justify-center gap-2 mt-1">
                                <a href={d.data} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-[#004030]" title="View"><Eye size={12} /></a>
                                <a href={d.data} download={d.name} className="text-slate-500 hover:text-[#004030]" title="Download"><Upload size={12} className="rotate-180" /></a>
                                <button type="button" onClick={() => removeAgentCert(d.id)} className="text-slate-500 hover:text-red-600" title="Delete"><Trash2 size={12} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Input type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} placeholder="Create password" className="h-11" data-testid="agent-reg-password" required />
                    {err && <p className="text-sm text-red-600">{err}</p>}
                    <Button type="submit" disabled={loading} data-testid="agent-register-submit" className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">{loading ? <Loader2 className="animate-spin" size={18} /> : "Send verification codes"}</Button>
                  </form>
                )
              ) : portal.isParent ? (
                <form onSubmit={doRegister} className="mt-5 space-y-3.5" data-testid="parent-register-form">
                  <Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="Your full name" className="h-11" required />
                  <Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="Email" className="h-11" required />
                  <div className="flex gap-2">
                    <Select value={reg.dial_code} onValueChange={(v) => setReg({ ...reg, dial_code: v })}>
                      <SelectTrigger className="h-11 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{dial.map((d) => <SelectItem key={d.code} value={d.dial}>{d.flag} {d.dial}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} placeholder="Phone" className="h-11 flex-1" required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Child's Student Reference Code *</Label>
                    <Input value={reg.student_code} onChange={(e) => setReg({ ...reg, student_code: e.target.value.toUpperCase() })} placeholder="e.g. GDIH-MY-000101" className="h-11" data-testid="parent-student-code" required />
                  </div>
                  <Input type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} placeholder="Create password" className="h-11" required />
                  {err && <p className="text-sm text-red-600">{err}</p>}
                  <Button type="submit" disabled={loading} data-testid="parent-register-submit" className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : "Link & create account"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={doRegister} className="mt-5 space-y-3.5">
                  <Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="Full name" className="h-11" required />
                  <Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="Email" className="h-11" required />
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Country *</Label>
                    <Select value={reg.country_code} onValueChange={(v) => { const d = dial.find((x) => x.code === v); setReg({ ...reg, country_code: v, country: d?.name || "", dial_code: d?.dial || reg.dial_code }); }}>
                      <SelectTrigger className="h-11" data-testid="reg-country"><SelectValue placeholder="Select your country" /></SelectTrigger>
                      <SelectContent>{dial.map((d) => <SelectItem key={d.code} value={d.code}>{d.flag} {d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Select value={reg.dial_code} onValueChange={(v) => setReg({ ...reg, dial_code: v })}>
                      <SelectTrigger className="h-11 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{dial.map((d) => <SelectItem key={d.code} value={d.dial}>{d.flag} {d.dial}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} placeholder="Phone" className="h-11 flex-1" required />
                  </div>
                  <Input value={reg.interest} onChange={(e) => setReg({ ...reg, interest: e.target.value })} placeholder="Course of interest (e.g. MBBS)" className="h-11" />
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Referring Agent Code *</Label>
                    <Select value={reg.agent_code} onValueChange={(v) => setReg({ ...reg, agent_code: v })}>
                      <SelectTrigger className="h-11" data-testid="reg-agent-code"><SelectValue placeholder="Select agent or Direct" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DIRECT">Direct (no agent)</SelectItem>
                        {agents.map((a) => <SelectItem key={a.agent_code} value={a.agent_code}>{a.agent_code} — {a.name}{a.agency_name ? ` (${a.agency_name})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} placeholder="Create password" className="h-11" required />
                  {err && <p className="text-sm text-red-600">{err}</p>}
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : "Create account"}
                  </Button>
                </form>
              )}
            </div>
          )}

          {/* OTP */}
          {portal && mode === "verify" && (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8" data-testid="verify-email-card">
              <h2 className="font-display text-2xl font-bold text-[#012a1e]">Verify your email</h2>
              <p className="text-slate-500 text-sm mt-1">We sent a 6-digit code to <b>{verifyInfo?.email}</b>. Enter it below to activate your account.</p>
              {verifyInfo?.student_code && <p className="text-sm mt-2 text-[#004030]">Your Student ID: <b>{verifyInfo.student_code}</b></p>}
              {verifyInfo?.demo_code && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  <b>DEMO MODE</b> — your code is <b data-testid="demo-verify-code">{verifyInfo.demo_code}</b> (live email once Resend key is added)
                </div>
              )}
              <form onSubmit={doVerifyEmail} className="mt-5 space-y-4">
                <Input value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} placeholder="Enter 6-digit code" className="h-12 text-center tracking-widest" data-testid="verify-code-input" required />
                {err && <p className="text-sm text-red-600">{err}</p>}
                <Button type="submit" disabled={loading} data-testid="verify-submit" className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & activate"}
                </Button>
                <button type="button" onClick={resendVerify} className="w-full text-sm text-slate-500 hover:text-[#004030]">Resend code</button>
                <button type="button" onClick={() => { setMode("login"); setErr(""); }} className="w-full text-sm text-slate-400 hover:text-[#004030]">Back to login</button>
              </form>
            </div>
          )}

          {portal && mode === "otp" && (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
              <div className="w-14 h-14 rounded-full bg-[#004030] grid place-items-center text-[#e0c05a] mx-auto"><KeyRound size={26} /></div>
              <h2 className="font-display text-2xl font-bold text-[#012a1e] mt-4 text-center">Verify it's you</h2>
              <p className="text-slate-500 text-sm mt-1 text-center">Enter the codes sent to your email &amp; phone</p>
              {(otpState?.email_delivered || otpState?.sms_delivered) && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
                  ✓ Code sent to {otpState?.email_delivered ? <b>your email</b> : null}
                  {otpState?.email_delivered && otpState?.sms_delivered ? " and " : null}
                  {otpState?.sms_delivered ? <b>your phone ({otpState?.phone_masked})</b> : null}. Check and enter below.
                </div>
              )}
              {(otpState?.demo_email_otp || otpState?.demo_phone_otp) && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  <b>DEMO MODE</b>
                  {otpState?.demo_email_otp ? <> — Email OTP: <b>{otpState?.demo_email_otp}</b></> : null}
                  {otpState?.demo_phone_otp ? <> · Phone OTP: <b>{otpState?.demo_phone_otp}</b></> : null}
                  <br />(Live SMS/email via Twilio &amp; Resend once keys are added)
                </div>
              )}
              <form onSubmit={doVerify} className="mt-5 space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Mail size={14} /> Email OTP</Label>
                  <Input value={otp.email_otp} onChange={(e) => setOtp({ ...otp, email_otp: e.target.value })} placeholder="6-digit code" className="h-12 tracking-[0.4em] text-center" />
                </div>
                {(otpState?.phone_required !== false) && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Smartphone size={14} /> Phone OTP <span className="text-xs text-slate-400">(first login only)</span></Label>
                    <Input value={otp.phone_otp} onChange={(e) => setOtp({ ...otp, phone_otp: e.target.value })} placeholder="6-digit code" className="h-12 tracking-[0.4em] text-center" data-testid="login-phone-otp" />
                  </div>
                )}
                {err && <p className="text-sm text-red-600">{err}</p>}
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full font-semibold">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & Sign in"}
                </Button>
                <button type="button" onClick={() => { setMode("login"); setErr(""); }} className="w-full text-sm text-slate-500 hover:text-[#004030]">Back to login</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
